import { Navigate, useParams } from 'react-router';
import { Suspense } from 'react';

import { IRoutes } from '@3.0/Layouts/TabbedLayout/utils/constants/routes.interface';
import { StudioRoutes } from 'Routing/RouteConstants';
import { RouteErrorPage, ChunkErrorBoundary } from '@components/ErrorBoundary';
import { LoadingIndicator } from '@components/LoadingIndicatorMaterial';
import { notFoundRoute } from './404.routes';

const EditQuestionRedirect = () => {
  const params = useParams();
  return <Navigate to={`${StudioRoutes.base}/${StudioRoutes.questions.base}/${params.queryId}`} />;
};

export const editQuestionRoutes: IRoutes[] = [
  {
    routeItem: {
      path: `${StudioRoutes.questions.base}/create-question${StudioRoutes.createQuestion.basePathVariables}`,
      children: [
        {
          path: '*',
          element: <EditQuestionRedirect />,
          errorElement: <RouteErrorPage />,
        },
      ],
    },
    permissionRef: StudioRoutes.createQuestion.permission,
    featureFlag: true,
  },

  {
    routeItem: {
      path: `${StudioRoutes.questions.base}/${StudioRoutes.createQuestion.base}${StudioRoutes.createQuestion.basePathVariables}`,
      children: [
        {
          path: '',
          hydrateFallbackElement: <LoadingIndicator isLoading={true} />,
          id: 'editQueryView',
          errorElement: <RouteErrorPage />,
          children: [
            {
              path: '',
              id: 'editQueryViewHome',
              errorElement: <RouteErrorPage />,
              async lazy() {
                const EditQuery = (await import('@components/Studio/QueryCreationTwo/EditQuery'))
                  .default;

                return {
                  element: (
                    <ChunkErrorBoundary>
                      <Suspense fallback={<LoadingIndicator isLoading />}>
                        <EditQuery />
                      </Suspense>
                    </ChunkErrorBoundary>
                  ),
                };
              },
            },
            {
              path: StudioRoutes.editQuestionMapping.base,
              id: 'editQuestionMappingView',
              errorElement: <RouteErrorPage />,
              hydrateFallbackElement: <LoadingIndicator isLoading={true} />,
              async lazy() {
                const EditQuestionMappingView = (
                  await import('Modules/Studio/Mappings/EditQuestionMappingView')
                ).default;

                const { fetchMappingListByQueryId } = await import(
                  'Modules/Studio/Mappings/routing/fetchMappingListByQueryId.loader'
                );

                return {
                  element: (
                    <ChunkErrorBoundary>
                      <Suspense fallback={<LoadingIndicator isLoading />}>
                        <EditQuestionMappingView />
                      </Suspense>
                    </ChunkErrorBoundary>
                  ),
                  loader: fetchMappingListByQueryId,
                };
              },
            },
            {
              path: StudioRoutes.editQuestionPreview.base,
              errorElement: <RouteErrorPage />,
              hydrateFallbackElement: <LoadingIndicator isLoading={true} />,
              async lazy() {
                const QuestionPreview = (
                  await import(
                    'Modules/Studio/Questions/EditQuestion/QuestionPreview/QuestionPreview'
                  )
                ).default;

                return {
                  element: (
                    <ChunkErrorBoundary>
                      <Suspense fallback={<LoadingIndicator isLoading />}>
                        <QuestionPreview />
                      </Suspense>
                    </ChunkErrorBoundary>
                  ),
                };
              },
            },
          ],
          async lazy() {
            const EditQueryLayout = (
              await import('Modules/Studio/Questions/EditQuestion/Layout/EditQueryLayout')
            ).default;

            return {
              element: (
                <ChunkErrorBoundary>
                  <Suspense fallback={<LoadingIndicator isLoading />}>
                    <EditQueryLayout />
                  </Suspense>
                </ChunkErrorBoundary>
              ),
            };
          },
        },
        notFoundRoute.routeItem,
      ],
    },
    permissionRef: StudioRoutes.createQuestion.permission,
    featureFlag: true,
  },
];
